import React from 'react';
import { LoginForm } from '../components/forms/LoginForm';
import { useNavigate } from 'react-router-dom';
import { BrandFooter } from '../components/ui/BrandFooter';
import { BrandMark } from '../components/ui/BrandMark';
import { useBranding } from '../context/BrandingContext';
import { Button } from '../components/ui/Button';

/**
 * Login Page Component
 * 
 * Modern, clean login page with branding and professional design
 * 
 * @returns {JSX.Element} Login page component
 */
export const Login: React.FC = () => {
  const { branding } = useBranding();


  const navigate = useNavigate()
  return (
    <div className="min-h-screen bg-white">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {/* Left Panel - Product Story */}
        <div className="relative hidden lg:flex">
          <div className="relative flex w-full flex-col overflow-hidden bg-gradient-to-br from-c-2b2458 via-c-4b63c9 to-c-3c95d9 p-10 text-white">
            <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-c-9e8bff/35 blur-3xl" />
            <div className="absolute -bottom-28 right-[-60px] h-96 w-96 rounded-full bg-c-59c6f9/25 blur-3xl" />
            <div className="absolute right-16 top-20 h-40 w-40 rounded-full border border-white/20" />

            <div className="relative flex h-full flex-col">
              <div>
                <p className="inline-flex rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-white/90">
                  {branding.productName}
                </p>
                <h2 className="mt-6 max-w-xl text-[2.4rem] font-semibold leading-[1.1]">
                  Build, monitor, and scale autonomous AI operations
                </h2>
                <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/85">
                  {branding.tagline}
                </p>
              </div>

              <div className="mt-8 grid grid-cols-3 gap-3 text-sm">
                <div className="rounded-2xl border border-white/25 bg-white/10 px-4 py-3">Live Agent Health</div>
                <div className="rounded-2xl border border-white/25 bg-white/10 px-4 py-3">Workflow Orchestration</div>
                <div className="rounded-2xl border border-white/25 bg-white/10 px-4 py-3">Secure Multi-tenant Ops</div>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur-sm">
                  <p className="text-xs font-semibold tracking-[0.12em] text-white/80">ACTIVE AGENTS</p>
                  <p className="mt-2 text-4xl font-semibold leading-none">148</p>
                  <p className="mt-2 text-sm text-white/80">Across support, onboarding, and operations</p>
                </div>
                <div className="rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur-sm">
                  <p className="text-xs font-semibold tracking-[0.12em] text-white/80">TASKS AUTOMATED</p>
                  <p className="mt-2 text-4xl font-semibold leading-none">26.8K</p>
                  <p className="mt-2 text-sm text-white/80">In the last 30 days</p>
                </div>
              </div>

              <div className="relative mt-8 rounded-2xl border border-white/20 bg-c-1f204d/40 p-5 backdrop-blur-sm">
                <p className="text-xs font-semibold tracking-[0.12em] text-white/70">OPERATION SNAPSHOT</p>
                <div className="mt-4 space-y-3 text-sm">
                  <div className="flex items-center justify-between rounded-xl bg-white/10 px-3 py-2">
                    <span>Intent Classification</span>
                    <span className="font-semibold text-[#A9F6C8]">99.2%</span>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-white/10 px-3 py-2">
                    <span>Response SLA</span>
                    <span className="font-semibold text-c-7fe3ff">1.8s avg</span>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-white/10 px-3 py-2">
                    <span>Escalation Accuracy</span>
                    <span className="font-semibold text-c-d6b8ff">97.6%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Form */}
        <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
          <div className="w-full max-w-md">
            <div className="flex items-center mb-6">
              <BrandMark className="h-16 w-auto" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1">
              {branding.productName ? `Welcome to ${branding.productName}` : 'Welcome'}
            </h1>
            <p className="text-gray-600 mb-8">Sign in to manage AI agents, workflows, and platform insights.</p>

            <div className="bg-white rounded-2xl p-0">
              <LoginForm />
            </div>

            <div className="mt-10 text-center text-sm text-gray-500">
              Don't have an account? <Button tone="custom" onClick={()=>{navigate("/signup")}} className="text-agentic-primary-blue hover:text-agentic-primary-blue-dark font-medium">Create Account</Button>
            </div>

            <BrandFooter />
          </div>
        </div>
      </div>
    </div>
  );
};
